/**
 * Public certificate verification.
 *
 * This is the page behind the QR code printed on every certificate the mahallu
 * issues. Whoever opens it is a person holding a piece of paper -- a receiving
 * mahallu, a school office, a government clerk -- not a signed-in member of
 * staff. So it renders before the session gate, on its own, and it asks the
 * one public endpoint that exists.
 *
 * It shows the register number, type, date and status, and nothing else. That
 * is enough to tell a genuine certificate from a forged one without
 * republishing the record it certifies (registers skill).
 */
import { CheckCircle2, FileWarning, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { API_BASE_URL } from "@/lib/api/client";
import { formatDate } from "@/lib/format";
import type { Locale } from "@/lib/i18n";

/** The entire public surface. There is no personal data here, by design. */
interface Verification {
  certificate_id: string;
  issue_no: string;
  issued_on: string;
  register_number: string;
  register_type: string;
  status: string;
  event_on: string;
}

type State =
  | { kind: "loading" }
  | { kind: "found"; data: Verification }
  | { kind: "missing" }
  | { kind: "error" };

/** `/verify/<uuid>` — the path the QR code encodes. */
export function verificationIdFromPath(pathname: string): string | null {
  const match = /^\/verify\/(.+?)\/?$/.exec(pathname);
  return match ? match[1]! : null;
}

/** Whether a code from the printed URL is even the right shape. */
export function isVerificationId(value: string): boolean {
  return /^[0-9a-fA-F-]{36}$/.test(value);
}

const TONE: Record<string, "success" | "warning" | "danger"> = {
  FINALIZED: "success",
  AMENDED: "warning",
  VOIDED: "danger",
};

export function VerifyPage({ certificateId }: { certificateId: string }) {
  const { t, i18n } = useTranslation("registers");
  const locale: Locale = i18n.language.startsWith("ml") ? "ml" : "en";
  // A code that is not even the right shape is a mistyped or misread one --
  // answered here rather than sent to the server. Anything else fell through
  // the router to the staff sign-in screen, which is the last thing to show
  // someone holding a piece of paper and no account.
  const malformed = !isVerificationId(certificateId);
  const [state, setState] = useState<State>(
    malformed ? { kind: "missing" } : { kind: "loading" },
  );

  useEffect(() => {
    if (malformed) return;
    const controller = new AbortController();
    // Plain fetch, not the typed client: that one attaches an access token
    // and retries through a refresh, and this page has no session at all.
    fetch(`${API_BASE_URL}/api/v1/verify/${certificateId}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 404) return setState({ kind: "missing" });
        if (!response.ok) return setState({ kind: "error" });
        setState({
          kind: "found",
          data: (await response.json()) as Verification,
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ kind: "error" });
      });
    return () => controller.abort();
  }, [certificateId, malformed]);

  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <div className="w-full max-w-lg rounded-md border border-card-edge bg-surface p-6 shadow-card sm:p-8">
        <h1 className="text-lg font-semibold">{t("verify.title")}</h1>
        <p className="mt-1 text-md text-ink-muted">{t("verify.intro")}</p>

        <div className="mt-6">
          {state.kind === "loading" ? (
            <p className="text-md text-ink-muted" aria-live="polite">
              {t("verify.checking")}
            </p>
          ) : state.kind === "missing" ? (
            <Outcome
              icon={<ShieldAlert className="size-5 text-danger" aria-hidden />}
              title={t("verify.notFound")}
              body={t("verify.notFoundBody")}
            />
          ) : state.kind === "error" ? (
            <Outcome
              icon={<FileWarning className="size-5 text-warning" aria-hidden />}
              title={t("verify.unavailable")}
              body={t("verify.unavailableBody")}
            />
          ) : (
            <Found data={state.data} locale={locale} />
          )}
        </div>
      </div>
    </main>
  );
}

function Outcome({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex gap-3 rounded-md border border-line-faint bg-surface-sunken p-4">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="text-md font-medium">{title}</p>
        <p className="mt-1 text-sm text-ink-muted">{body}</p>
      </div>
    </div>
  );
}

function Found({ data, locale }: { data: Verification; locale: Locale }) {
  const { t } = useTranslation("registers");
  const voided = data.status === "VOIDED";

  const rows: Array<[string, React.ReactNode]> = [
    [
      t("column.number"),
      <span className="tabular">{data.register_number}</span>,
    ],
    [t("column.type"), t(`type.${data.register_type}`)],
    [t("column.date"), formatDate(data.event_on, locale)],
    [t("verify.issuedOn"), formatDate(data.issued_on, locale)],
    // A reprint is not an original. Saying which copy this is, is part of
    // what makes the certificate hard to pass off.
    [t("verify.issueNo"), <span className="tabular">{data.issue_no}</span>],
  ];

  return (
    <>
      <div
        className={
          voided
            ? "flex gap-3 rounded-md border border-danger-soft bg-danger-soft p-4"
            : "flex gap-3 rounded-md border border-success-soft bg-success-soft p-4"
        }
      >
        <span className="mt-0.5 shrink-0">
          {voided ? (
            <ShieldAlert className="size-5 text-danger-soft-ink" aria-hidden />
          ) : (
            <CheckCircle2
              className="size-5 text-success-soft-ink"
              aria-hidden
            />
          )}
        </span>
        <div>
          <p
            className={
              voided
                ? "text-md font-medium text-danger-soft-ink"
                : "text-md font-medium text-success-soft-ink"
            }
          >
            {voided ? t("verify.voided") : t("verify.genuine")}
          </p>
          <p
            className={
              voided
                ? "mt-1 text-sm text-danger-soft-ink"
                : "mt-1 text-sm text-success-soft-ink"
            }
          >
            {voided
              ? t("verify.voidedBody")
              : data.status === "AMENDED"
                ? t("verify.amendedBody")
                : t("verify.genuineBody")}
          </p>
        </div>
      </div>

      <dl className="mt-5 divide-y divide-line-faint">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2.5">
            <dt className="text-sm text-ink-muted">{label}</dt>
            <dd className="text-sm font-medium">{value}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-4 py-2.5">
          <dt className="text-sm text-ink-muted">{t("column.status")}</dt>
          <dd>
            <Badge tone={TONE[data.status] ?? "neutral"}>
              {t(`status.${data.status}`)}
            </Badge>
          </dd>
        </div>
      </dl>

      <p className="mt-5 text-2xs text-ink-subtle">{t("verify.privacyNote")}</p>
    </>
  );
}
